import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useCotizacionEstandarStore } from "@/store/cotizaciones/estandar/";
import useCotizacionEstandarLeaseParametersStore from "@/store/cotizaciones/estandar/lease-parameters";
import useCotizacionEstandarKeyMetricsStore from "@/store/cotizaciones/estandar/key-metrics";

const useCotizacionEstandarSourcesStore = defineStore("cotEstSources", () => {
  const { stateCalculosLeaseParameters, state } = storeToRefs(
    useCotizacionEstandarStore()
  );
  const { leaseParameters } = storeToRefs(
    useCotizacionEstandarLeaseParametersStore()
  );
  const { keyMetrics } = storeToRefs(useCotizacionEstandarKeyMetricsStore());

  const sources = computed(() => {
    const equiFacilityDraw = ref(
      leaseParameters.value.equipmentValueBeforeNet.value -
        leaseParameters.value.downPaymentMonto.value
    );
    const equiLesseeContri = ref(leaseParameters.value.downPaymentMonto.value);
    const equiTotal = ref(equiFacilityDraw.value + equiLesseeContri.value);

    const servLesseeContri = ref(
      (stateCalculosLeaseParameters.value.serviceContribution / 100) *
        keyMetrics.value.monthlyPayment.value
    );

    const equipment = {
      equipment: {
        facilityDraw: equiFacilityDraw.value,
        lesseeContri: equiLesseeContri.value,
        total: equiTotal.value,
      },
      equipmentVAT: {
        facilityDraw:
          leaseParameters.value.vatMonto.value -
          leaseParameters.value.downPaymentAmtVAT.value,
        lesseeContri: leaseParameters.value.downPaymentAmtVAT.value,
        total:
          leaseParameters.value.vatMonto.value -
          leaseParameters.value.downPaymentAmtVAT.value +
          leaseParameters.value.downPaymentAmtVAT.value,
      },
      servicerContribution: {
        facilityDraw: -servLesseeContri.value,
        lesseeContri: servLesseeContri.value,
        total: -servLesseeContri.value + servLesseeContri.value,
      },
      totalPaid: {
        facilityDraw: -servLesseeContri.value,
        lesseeContri: servLesseeContri.value,
        total: -servLesseeContri.value + servLesseeContri.value,
      },
    };

    const servicerFees = {
      openingFee: {
        lesseeContri: leaseParameters.value.openingFeeMonto.value,
        total:
          parseFloat(stateCalculosLeaseParameters.value.opFeeFacility) +
          parseFloat(leaseParameters.value.openingFeeMonto.value),
      },
      vatOpeningFee: {
        lesseeContri:
          leaseParameters.value.openingFeeMonto.value *
          (stateCalculosLeaseParameters.value.vat / 100),
        total:
          parseFloat(stateCalculosLeaseParameters.value.vatOpFeeFacility) +
          parseFloat(
            leaseParameters.value.openingFeeMonto.value *
              (stateCalculosLeaseParameters.value.vat / 100)
          ),
      },
    };

    const firstMonthsRentLesseeContri = ref(
      parseFloat(leaseParameters.value.rentUpFront.value) > 0
        ? keyMetrics.value.monthlyPayment.value *
            (parseFloat(leaseParameters.value.rentUpFront.value) / 100)
        : 0
    );

    const firstMonthsRent = {
      firstMonthsRent: {
        lesseeContri: firstMonthsRentLesseeContri.value,
        total:
          parseFloat(stateCalculosLeaseParameters.value.firstMonthsRent) +
          firstMonthsRentLesseeContri.value,
      },
      vatOnOpeningFee: {
        lesseeContri:
          firstMonthsRentLesseeContri.value *
          (parseFloat(stateCalculosLeaseParameters.value.vat) / 100),
        total:
          parseFloat(stateCalculosLeaseParameters.value.firstMonthVATOpFee) +
          firstMonthsRentLesseeContri.value *
            (parseFloat(stateCalculosLeaseParameters.value.vat) / 100),
      },
    };

    const daysDiff = computed(() => {
      const closing = new Date(state.value.fechaCierre);
      const firstPayment = new Date(state.value.fechaPrimerVencimiento);

      const diffTime = firstPayment.getTime() - closing.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    });

    const partialDayRentLessee = ref(
      (parseFloat(leaseParameters.value.partialDayInterestRate.value / 100) /
        360) *
        daysDiff.value *
        keyMetrics.value.leasePrincipalAmount.value
    );

    const partialDayInterest = {
      partialDayRent: {
        lesseeContri: partialDayRentLessee.value,
        total:
          parseFloat(stateCalculosLeaseParameters.value.partialDayRent) +
          partialDayRentLessee.value,
      },
      partialDayVAT: {
        lesseeContri:
          (partialDayRentLessee.value *
            parseFloat(stateCalculosLeaseParameters.value.vat)) /
          100,
        total:
          parseFloat(stateCalculosLeaseParameters.value.partialDayVAT) +
          (partialDayRentLessee.value *
            parseFloat(stateCalculosLeaseParameters.value.vat)) /
            100,
      },
    };

    return {
      equipment,
      servicerFees,
      firstMonthsRent,
      partialDayInterest,
    };
  });

  const {
    equipment,
    equipmentVAT,
    servicerContribution,
  } = sources.value.equipment;

  const { openingFee, vatOpeningFee } = sources.value.servicerFees;

  const { firstMonthsRent, vatOnOpeningFee } = sources.value.firstMonthsRent;

  const { partialDayRent, partialDayVAT } = sources.value.partialDayInterest;

  const total = computed(() => {
    return {
      equipment: {
        facilityDraw:
          equipment.facilityDraw +
          equipmentVAT.facilityDraw +
          servicerContribution.facilityDraw,
        lesseeContri:
          equipment.lesseeContri +
          equipmentVAT.lesseeContri +
          servicerContribution.lesseeContri,
        total:
          equipment.total + equipmentVAT.total + servicerContribution.total,
      },
      equipmentVAT: {
        facilityDraw:
          parseFloat(stateCalculosLeaseParameters.value.opFeeFacility) +
          parseFloat(stateCalculosLeaseParameters.value.vatOpFeeFacility),
        lesseeContri: openingFee.lesseeContri + vatOpeningFee.lesseeContri,
        total: openingFee.total + vatOpeningFee.total,
      },
      firstMonthsRent: {
        facilityDraw:
          parseFloat(stateCalculosLeaseParameters.value.firstMonthsRent) +
          parseFloat(stateCalculosLeaseParameters.value.firstMonthVATOpFee),
        lesseeContri:
          firstMonthsRent.lesseeContri + vatOnOpeningFee.lesseeContri,
        total: firstMonthsRent.total + vatOnOpeningFee.total,
      },
      partialDayInterest: {
        facilityDraw:
          parseFloat(stateCalculosLeaseParameters.value.partialDayRent) +
          parseFloat(stateCalculosLeaseParameters.value.partialDayVAT),
        lesseeContri: partialDayRent.lesseeContri + partialDayVAT.lesseeContri,
        total: partialDayRent.total + partialDayVAT.total,
      },
    };
  });

  const totalSources = computed(() => {
    return {
      facilityDraw:
        total.value.equipment.facilityDraw +
        total.value.equipmentVAT.facilityDraw +
        total.value.firstMonthsRent.facilityDraw +
        total.value.partialDayInterest.facilityDraw,
      lesseeContri:
        total.value.equipment.lesseeContri +
        total.value.equipmentVAT.lesseeContri +
        total.value.firstMonthsRent.lesseeContri +
        total.value.partialDayInterest.lesseeContri,
      total:
        total.value.equipment.total +
        total.value.equipmentVAT.total +
        total.value.firstMonthsRent.total +
        total.value.partialDayInterest.total,
    };
  });

  const netVat = computed(() => {
    return (
      leaseParameters.value.vatMonto.value -
      leaseParameters.value.downPaymentAmtVAT.value -
      sources.value.servicerFees.vatOpeningFee.lesseeContri -
      sources.value.firstMonthsRent.vatOnOpeningFee.lesseeContri -
      sources.value.partialDayInterest.partialDayVAT.lesseeContri
    ).toFixed(2);
  });

  console.log(netVat.value);

  return {
    sources,
    total,
    totalSources,
    netVat,
  };
});

export default useCotizacionEstandarSourcesStore;
